import ExpandMore from '@efex-components/common/ExpandMore';
import clientSvc from '@efex-services/client/clientSvc';
import { useApiCall } from '@efexteam/use-api-call';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  Skeleton,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import fundingSvc from '@efex-services/funds/fundingSvc';
import { ActionHistory } from '@efex-services/payments/types';
import {
  formatDate,
  getMovementTypeLabel,
  maskValue,
} from './constants/utilities';
import DetailCardStyles from './DetailCard.style';
import { DetailCardProps, DetailRowProps } from './types/detailCard.type';

const DetailRow = ({ label, value, isLink = false }: DetailRowProps) => (
  <Box className={DetailCardStyles.contentItemRow}>
    <Typography variant="caption" className="flex-[0.5]">
      {label}
    </Typography>
    {isLink ? (
      <Typography
        variant="body2"
        className={DetailCardStyles.contentItemRowLink}
      >
        <a href={value as string} target="_blank" rel="noopener noreferrer">
          {(value as string).split('?')[0]}
        </a>
      </Typography>
    ) : (
      <Typography variant="body2" className="flex-1">
        {value || '---'}
      </Typography>
    )}
  </Box>
);

const DetailCard = ({ transfer, isLoading }: DetailCardProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const {
    data: paymentDetail,
    loading: paymentDetailLoading,
    invoke: getPaymentDetail,
  } = useApiCall(() =>
    clientSvc.getPaymentDetail({
      identify: transfer?.uuid || '',
      origin: 'payable',
    })
  );

  const { data: paymentReasons, invoke: getPaymentReasons } = useApiCall(() =>
    fundingSvc.getPaymentReasons()
  );

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const actionCreate = useMemo(
    () =>
      transfer?.action_history?.find(
        (action) => action.action === ActionHistory.CREATE_PAYMENT
      ),
    [transfer]
  );

  const actionApprove = useMemo(
    () =>
      transfer?.action_history?.find(
        (action) => action.action === ActionHistory.APPROVE_PAYMENT
      ),
    [transfer]
  );

  useEffect(() => {
    if (transfer && transfer.uuid && !paymentDetail) {
      getPaymentReasons();
      getPaymentDetail();
    }
  }, [transfer, getPaymentDetail, paymentDetail, getPaymentReasons]);

  if (isLoading) {
    return (
      <Card className={DetailCardStyles.wrapper}>
        <Skeleton variant="rectangular" height={100} />
      </Card>
    );
  }

  if (!transfer) {
    return (
      <Card className={DetailCardStyles.wrapper}>
        <CardContent>
          <Typography variant="h6">{t('trsfDtlDetailCardTitle')}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={DetailCardStyles.wrapper}>
      <CardHeader
        title={
          <Typography variant="h6">{t('trsfDtlDetailCardTitle')}</Typography>
        }
        action={
          <ExpandMore
            expand={expanded}
            onClick={handleExpandClick}
            aria-expanded={expanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </ExpandMore>
        }
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent className={DetailCardStyles.content}>
          <Divider />
          <Box className={DetailCardStyles.contentItem}>
            <DetailRow
              label={t('trsfDtlConfigurator')}
              value={
                actionCreate
                  ? `${actionCreate?.user_first_name} ${actionCreate?.user_last_name}`
                  : '---'
              }
            />
            <DetailRow
              label={t('trsfDtlApprover')}
              value={
                actionApprove
                  ? `${actionApprove?.user_first_name} ${actionApprove?.user_last_name}`
                  : '---'
              }
            />
            <DetailRow
              label={t('trsfDtlConfiguredDate')}
              value={
                actionCreate ? formatDate(actionCreate?.created_at) : '---'
              }
            />
            <DetailRow
              label={t('trsfDtlApprovalDate')}
              value={
                actionApprove ? formatDate(actionApprove?.created_at) : '---'
              }
            />
            <DetailRow
              label={t('trsfDtlSentDate')}
              value={formatDate(transfer.executionDate)}
            />
          </Box>
          <Divider />
          <Box className={DetailCardStyles.contentItem}>
            <DetailRow
              label={t('trsfDtlRecipient')}
              value={transfer.contact?.enterprise?.name}
            />
            <DetailRow
              label={t('trsfDtlAccountNumber')}
              value={maskValue(transfer.enterpriseAccount?.accountNumber)}
            />
            <DetailRow
              label={t('trsfDtlSwiftCode')}
              value={maskValue(transfer.enterpriseAccount?.swiftCode)}
            />
            <DetailRow
              label={t('trsfDtlMovementType')}
              value={t(getMovementTypeLabel(transfer.paymentMethodId))}
            />
            <DetailRow
              label={t('trsfDtlReason')}
              value={
                transfer.paymentReason && paymentReasons
                  ? t(
                      paymentReasons.find(
                        (reason) => reason.id === Number(transfer.paymentReason)
                      )?.name || '---'
                    )
                  : '---'
              }
            />
          </Box>
          <Divider />
          <Box className={DetailCardStyles.contentItem}>
            {paymentDetailLoading && (
              <Skeleton variant="text" width={100} height={20} />
            )}
            {paymentDetail &&
              paymentDetail.invoice_files &&
              paymentDetail.invoice_files.map((file) => (
                <DetailRow
                  key={file}
                  label={t('trsfDtlAttachment')}
                  value={file}
                  isLink
                />
              ))}
            {paymentDetail && paymentDetail.cep && (
              <DetailRow
                label={t('trsfDtlCep')}
                value={paymentDetail.cep}
                isLink
              />
            )}
            <DetailRow label={t('trsfDtlNote')} value={transfer.comment} />
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default DetailCard;
